/**
 * Jornada laboral pactada en el contrato: horas semanales y reglas legales.
 *
 * Lo usan el formulario de contratos (avisa mientras se completa) y el backend
 * (rechaza lo que no cumple). Si cada uno tuviera su propia copia de las reglas,
 * terminarían diciendo cosas distintas.
 *
 * CommonJS y sin spread, for...of ni desestructuración de arrays: el backend lo
 * carga con require y el frontend con import, y esa sintaxis haría que el build de
 * React le inyecte imports de helpers que no conviven con module.exports.
 */

const DIAS_SEMANA = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO', 'DOMINGO'];

const NOMBRE_DIA = {
    LUNES: 'lunes',
    MARTES: 'martes',
    MIERCOLES: 'miércoles',
    JUEVES: 'jueves',
    VIERNES: 'viernes',
    SABADO: 'sábado',
    DOMINGO: 'domingo',
};

const JORNADAS = ['COMPLETA', 'PARCIAL', 'EXCLUIDA'];
const TIPOS_TURNO = ['FIJO', 'ROTATIVO'];

// Ley 21.561: la jornada ordinaria máxima baja por etapas. Rige la vigente a la
// fecha de inicio del contrato, que puede ser de hace años.
const TRAMOS_MAXIMO_SEMANAL = [
    { desde: '2028-04-26', horas: 40 },
    { desde: '2026-04-26', horas: 42 },
    { desde: '2024-04-26', horas: 44 },
];
const MAXIMO_ANTES_LEY_21561 = 45;

const MAX_HORAS_DIA = 10;       // art. 28
const MAX_DIAS_SEMANA = 6;      // art. 28
const MIN_COLACION = 30;        // art. 34
const FRACCION_PARCIAL = 2 / 3; // art. 40 bis

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

const mayusculaInicial = (t) => t.charAt(0).toUpperCase() + t.slice(1);

function fechaISO(fecha) {
    if (fecha instanceof Date) {
        const mm = String(fecha.getMonth() + 1).padStart(2, '0');
        const dd = String(fecha.getDate()).padStart(2, '0');
        return `${fecha.getFullYear()}-${mm}-${dd}`;
    }
    return texto(fecha).slice(0, 10);
}

/** Máximo semanal vigente a la fecha ('YYYY-MM-DD' o Date); sin fecha, el de hoy. */
function maxHorasSemanales(fecha) {
    const iso = fechaISO(fecha) || fechaISO(new Date());
    const tramo = TRAMOS_MAXIMO_SEMANAL.find((t) => iso >= t.desde);
    return tramo ? tramo.horas : MAXIMO_ANTES_LEY_21561;
}

/** Tope de la jornada parcial: dos tercios del máximo semanal vigente a la fecha. */
function maxHorasParcial(fecha) {
    return maxHorasSemanales(fecha) * FRACCION_PARCIAL;
}

/** 'HH:MM' → minutos desde medianoche; NaN si no es una hora válida. */
function aMinutos(hora) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(texto(hora));
    if (!m) return NaN;
    const h = Number(m[1]);
    const min = Number(m[2]);
    return h < 24 && min < 60 ? h * 60 + min : NaN;
}

/**
 * Minutos trabajados en un día: de la entrada a la salida, menos la colación.
 * Una salida anterior a la entrada es un turno que cruza la medianoche.
 */
function minutosTrabajadosDia(dia) {
    const entrada = aMinutos(dia.entrada);
    const salida = aMinutos(dia.salida);
    if (Number.isNaN(entrada) || Number.isNaN(salida)) return NaN;
    const presencia = salida > entrada ? salida - entrada : salida + 24 * 60 - entrada;
    return presencia - (Number(dia.colacion) || 0);
}

/** Horas semanales de un horario fijo, con dos decimales (41 h 30 min → 41.5). */
function calcularHorasSemanales(horario) {
    const minutos = (horario || []).reduce((total, dia) => total + minutosTrabajadosDia(dia), 0);
    return Math.round((minutos / 60) * 100) / 100;
}

/** 41.5 → "41 horas y 30 minutos"; con corto, "41 h 30 min". */
function formatearHoras(horas, corto = false) {
    const total = Math.round(Number(horas) * 60);
    const h = Math.floor(total / 60);
    const min = total % 60;
    if (corto) return min ? `${h} h ${min} min` : `${h} h`;
    const parteHoras = `${h} ${h === 1 ? 'hora' : 'horas'}`;
    return min ? `${parteHoras} y ${min} minutos` : parteHoras;
}

function validarColacion(minutos, prefijo) {
    const n = Number(minutos) || 0;
    if (n < 0 || (n > 0 && n < MIN_COLACION)) {
        return [`${prefijo}la colación debe ser de 0 o de al menos ${MIN_COLACION} minutos (art. 34).`];
    }
    return [];
}

function validarHorarioFijo(horario) {
    const errores = [];
    if (horario.length === 0) errores.push('Marque al menos un día de trabajo.');
    if (horario.length > MAX_DIAS_SEMANA) {
        errores.push(`La jornada no puede distribuirse en más de ${MAX_DIAS_SEMANA} días a la semana (art. 28).`);
    }

    const vistos = {};
    horario.forEach((dia) => {
        if (!NOMBRE_DIA[dia.dia] || vistos[dia.dia]) {
            errores.push('El horario tiene un día no válido o repetido.');
            return;
        }
        vistos[dia.dia] = true;

        const prefijo = `${mayusculaInicial(NOMBRE_DIA[dia.dia])}: `;
        const entrada = aMinutos(dia.entrada);
        const salida = aMinutos(dia.salida);
        if (Number.isNaN(entrada) || Number.isNaN(salida)) {
            errores.push(`${prefijo}indique la hora de entrada y la de salida.`);
            return;
        }
        if (entrada === salida) {
            errores.push(`${prefijo}la salida no puede ser a la misma hora que la entrada.`);
            return;
        }

        const colacion = validarColacion(dia.colacion, prefijo);
        errores.push.apply(errores, colacion);
        if (colacion.length) return;

        const minutos = minutosTrabajadosDia(dia);
        if (minutos <= 0) {
            errores.push(`${prefijo}la colación ocupa todo el tiempo entre la entrada y la salida.`);
        } else if (minutos > MAX_HORAS_DIA * 60) {
            errores.push(`${prefijo}suma ${formatearHoras(minutos / 60)} de trabajo; el máximo es ${MAX_HORAS_DIA} horas diarias (art. 28).`);
        }
    });
    return errores;
}

/**
 * Horas semanales que corresponden a la jornada: las del horario si el turno es
 * fijo, las digitadas si es rotativo, ninguna si la jornada es excluida.
 */
function horasDeLaJornada(contrato) {
    if (contrato.jornada === 'EXCLUIDA') return null;
    if (contrato.tipo_turno === 'FIJO') return calcularHorasSemanales(contrato.horario);
    const horas = Number(contrato.horas_semanales);
    return Number.isFinite(horas) && horas > 0 ? horas : null;
}

/**
 * Errores de la jornada pactada; vacío si cumple. Recibe los campos del
 * formulario: jornada, tipo_turno, horario, horas_semanales, colacion_minutos,
 * descripcion_turnos y fecha_inicio.
 */
function validarJornada(contrato) {
    if (JORNADAS.indexOf(contrato.jornada) === -1) return ['Seleccione el tipo de jornada.'];
    if (contrato.jornada === 'EXCLUIDA') return [];
    if (TIPOS_TURNO.indexOf(contrato.tipo_turno) === -1) return ['Seleccione el tipo de turno.'];

    let errores;
    if (contrato.tipo_turno === 'FIJO') {
        errores = validarHorarioFijo(contrato.horario || []);
    } else {
        errores = [];
        if (!texto(contrato.descripcion_turnos)) errores.push('Describa el sistema de turnos.');
        errores.push.apply(errores, validarColacion(contrato.colacion_minutos, 'Turnos: '));
    }

    const horas = horasDeLaJornada(contrato);
    if (horas === null || horas <= 0) {
        if (contrato.tipo_turno === 'ROTATIVO') errores.push('Indique las horas semanales.');
        return errores;
    }

    const maximo = maxHorasSemanales(contrato.fecha_inicio);
    if (horas > maximo) {
        errores.push(`La jornada suma ${formatearHoras(horas)} semanales; el máximo legal a la fecha de inicio es de ${maximo} horas (Ley 21.561).`);
    }
    const maximoParcial = maxHorasParcial(contrato.fecha_inicio);
    if (contrato.jornada === 'PARCIAL' && horas > maximoParcial + 1e-9) {
        errores.push(`Una jornada parcial no puede superar las ${formatearHoras(maximoParcial)} semanales, dos tercios del máximo legal (art. 40 bis).`);
    }
    return errores;
}

/**
 * Lo que se guarda en el contrato: cada tipo de turno deja en nulo los campos que
 * no le corresponden, y el horario queda en orden de semana.
 */
function jornadaPactada(contrato) {
    const excluida = contrato.jornada === 'EXCLUIDA';
    const fijo = !excluida && contrato.tipo_turno === 'FIJO';
    const rotativo = !excluida && contrato.tipo_turno === 'ROTATIVO';
    const horario = fijo
        ? (contrato.horario || [])
            .map((d) => ({ dia: d.dia, entrada: texto(d.entrada), salida: texto(d.salida), colacion: Number(d.colacion) || 0 }))
            .sort((a, b) => DIAS_SEMANA.indexOf(a.dia) - DIAS_SEMANA.indexOf(b.dia))
        : null;

    return {
        jornada: contrato.jornada,
        tipo_turno: excluida ? null : contrato.tipo_turno,
        horario,
        horas_semanales: horasDeLaJornada(Object.assign({}, contrato, { horario })),
        colacion_minutos: rotativo ? Number(contrato.colacion_minutos) || 0 : null,
        descripcion_turnos: rotativo ? texto(contrato.descripcion_turnos) : null,
    };
}

module.exports = {
    DIAS_SEMANA,
    NOMBRE_DIA,
    JORNADAS,
    TIPOS_TURNO,
    maxHorasSemanales,
    maxHorasParcial,
    minutosTrabajadosDia,
    calcularHorasSemanales,
    formatearHoras,
    horasDeLaJornada,
    validarJornada,
    jornadaPactada,
};
